import { Box, Typography } from '@mui/material';

import { useCardImageAspectRatioStore, type WallBoard, type WallCard } from '@walls/store';
import { useCardImage } from './hooks/useCardImage';
import { ImageCardImage } from './ImageCardImage';
import {
  getCardHtml,
  getCardText,
  getCardTitleHtml,
  getCardTitleText,
  isImageCard,
} from './utils/cardDisplay';
import { MultilineText } from './utils/MultilineText';

interface CardBodyProps {
  board: WallBoard;
  card: WallCard;
  centered?: boolean;
  triangle?: boolean;
}

export function CardBody({ board, card, centered = false, triangle = false }: CardBodyProps) {
  const cardText = getCardText(card);
  const cardHtml = getCardHtml(card);
  const cardTitleText = getCardTitleText(card);
  const cardTitleHtml = getCardTitleHtml(card);
  const imageUrl = useCardImage(board, card);
  const setCardImageAspectRatio = useCardImageAspectRatioStore(
    (state) => state.setCardImageAspectRatio
  );

  if (isImageCard(card)) {
    return (
      <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column', minHeight: 0 }}>
        {imageUrl ? (
          <ImageCardImage
            alt={cardText || 'Card image'}
            src={imageUrl}
            onImageLoad={(image) => {
              const { naturalHeight, naturalWidth } = image;

              if (naturalHeight > 0 && naturalWidth > 0) {
                setCardImageAspectRatio(card.id, naturalWidth / naturalHeight);
              }
            }}
          />
        ) : (
          <Box sx={{ flex: 1, minHeight: 80 }} />
        )}
        {cardTitleHtml ? (
          <Typography
            variant="body2"
            sx={imageTitleStyles(centered)}
            dangerouslySetInnerHTML={{ __html: cardTitleHtml }}
          />
        ) : (
          cardTitleText && (
            <Typography variant="body2" sx={imageTitleStyles(centered)}>
              <MultilineText text={cardTitleText} />
            </Typography>
          )
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        alignItems: centered ? 'center' : 'flex-start',
        display: 'flex',
        flex: 1,
        justifyContent: centered ? 'center' : 'flex-start',
        minHeight: 38,
        p: triangle ? '42px 24px 18px' : 1.25,
        textAlign: centered ? 'center' : 'left',
        wordBreak: 'break-word',
      }}
    >
      {cardHtml ? (
        <Typography
          component="span"
          variant="body2"
          sx={cardTextStyles}
          dangerouslySetInnerHTML={{ __html: cardHtml }}
        />
      ) : (
        <Typography component="span" variant="body2" sx={cardTextStyles}>
          <MultilineText text={cardText} />
        </Typography>
      )}
    </Box>
  );
}

const cardTextStyles = {
  color: 'inherit',
  '& ul': {
    margin: 0,
    paddingLeft: '24px',
  },
};

function imageTitleStyles(centered: boolean) {
  return {
    color: 'inherit',
    flex: '0 0 auto',
    '& ul': {
      margin: 0,
    },
    p: 1.25,
    textAlign: centered ? 'center' : 'left',
    wordBreak: 'break-word',
  };
}
